<script setup>
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'

import LongText from '../../../components/LongText.vue'

const props = defineProps({
  request: { type: Object, required: true },
  response: { type: Object, default: null },
  provider: { type: String, required: true },
  status: { type: String, required: true },
  totalTokens: { type: Number, required: true },
  outputUrl: { type: String, default: null },
  startFrameUrl: { type: String, default: null },
  endFrameUrl: { type: String, default: null },
  inputPresentations: { type: Array, required: true },
})

const { t } = useI18n()
const requestJson = computed(() => JSON.stringify(props.request, null, 2))
const responseJson = computed(() => JSON.stringify(props.response, null, 2))
const prompt = computed(() => {
  switch (props.provider) {
    case 'seedance':
      return props.request.content.find(item => item.type === 'text').text
    case 'minimax_h3':
      return props.request.prompt
    default:
      throw new Error(`Unhandled video provider: ${props.provider}`)
  }
})
const requestFacts = computed(() => {
  switch (props.provider) {
    case 'seedance':
      return [
        fact('resolution', props.request.resolution),
        fact('ratio', props.request.ratio),
        fact('duration', `${props.request.duration} s`),
        fact('generateAudio', enabled(props.request.generate_audio)),
        fact('returnLastFrame', enabled(props.request.return_last_frame)),
        fact('watermark', enabled(props.request.watermark)),
      ]
    case 'minimax_h3':
      return [
        fact('resolution', `${props.request.width} × ${props.request.height}`),
        fact('duration', `${(props.request.frames / 24).toFixed(2)} s`),
        fact('framesPerSecond', '24'),
      ]
    default:
      throw new Error(`Unhandled video provider: ${props.provider}`)
  }
})
const responseFacts = computed(() => {
  switch (props.provider) {
    case 'seedance':
      return [
        fact('taskStatus', props.response.status),
        fact('seed', props.response.seed),
        fact('resolution', props.response.resolution),
        fact('ratio', props.response.ratio),
        fact('duration', `${props.response.duration} s`),
        fact('framesPerSecond', props.response.framespersecond),
        fact('serviceTier', props.response.service_tier),
        fact('totalTokens', props.totalTokens),
      ]
    case 'minimax_h3':
      return [
        fact('taskStatus', props.response.status),
        fact('seed', props.request.seed),
        fact('resolution', `${props.request.width} × ${props.request.height}`),
        fact('duration', `${(props.request.frames / 24).toFixed(2)} s`),
        fact('framesPerSecond', '24'),
        fact('serviceTier', props.response.partition),
        fact('totalTokens', props.totalTokens),
      ]
    default:
      throw new Error(`Unhandled video provider: ${props.provider}`)
  }
})

function fact(label, value) {
  return { label: t(`visualGameAdmin.llm.calls.detail.video.${label}`), value }
}

function enabled(value) {
  return value ? t('visualGameAdmin.llm.calls.detail.video.enabled') : t('visualGameAdmin.llm.calls.detail.video.disabled')
}

function inputLabel(input) {
  switch (input.source) {
    case 'game_asset':
      switch (input.role) {
        case 'first_frame':
          return t('visualGameAdmin.llm.calls.detail.video.firstFrame')
        case 'reference_image':
          return t('visualGameAdmin.llm.calls.detail.video.referenceImage')
        case 'reference_video':
          return t('visualGameAdmin.llm.calls.detail.video.referenceVideo')
        default:
          throw new Error(`Unhandled game media role: ${input.role}`)
      }
    case 'provider_asset':
      return t('visualGameAdmin.llm.calls.detail.video.providerAssetId')
    default:
      throw new Error(`Unhandled video input source: ${input.source}`)
  }
}

function inputAssetLabel(input) {
  switch (input.source) {
    case 'game_asset': {
      switch (input.media_type) {
        case 'image':
          return input.asset_tag === null
            ? t('visualGameAdmin.llm.calls.detail.video.gameImage')
            : `${t('visualGameAdmin.llm.calls.detail.video.gameImage')} · ${input.asset_tag}`
        case 'video':
          return t('visualGameAdmin.llm.calls.detail.video.continuityVideo')
        default:
          throw new Error(`Unhandled game media type: ${input.media_type}`)
      }
    }
    case 'provider_asset':
      return t('visualGameAdmin.llm.calls.detail.video.providerAssetId')
    default:
      throw new Error(`Unhandled video input source: ${input.source}`)
  }
}

function openInputLabel(input) {
  switch (input.media_type) {
    case 'image':
      return t('visualGameAdmin.llm.calls.detail.video.openImage')
    case 'video':
      return t('visualGameAdmin.llm.calls.detail.video.openVideo')
    default:
      throw new Error(`Unhandled input media type: ${input.media_type}`)
  }
}

</script>

<template>
  <section class="video-call-payload">
    <header class="video-call-payload__heading">
      <div>
        <span class="video-call-payload__eyebrow">{{ t('visualGameAdmin.llm.calls.detail.video.request.eyebrow') }}</span>
        <h3>{{ t('visualGameAdmin.llm.calls.detail.video.request.title') }}</h3>
      </div>
      <strong>{{ request.model }}</strong>
    </header>

    <dl class="video-call-payload__configuration">
      <div v-for="item in requestFacts" :key="item.label"><dt>{{ item.label }}</dt><dd>{{ item.value }}</dd></div>
    </dl>

    <LongText
      :label="t('visualGameAdmin.llm.calls.detail.video.prompt')"
      :text="prompt"
      :copy-text="prompt"
    />

    <section v-if="inputPresentations.length" class="video-call-payload__input-section">
      <header>
        <div>
          <span class="video-call-payload__eyebrow">{{ t('visualGameAdmin.llm.calls.detail.video.inputMedia.eyebrow') }}</span>
          <h4>{{ t('visualGameAdmin.llm.calls.detail.video.inputMedia.title') }}</h4>
        </div>
        <span>{{ inputPresentations.length }}</span>
      </header>
      <div class="video-call-payload__input-grid">
        <figure v-for="item in inputPresentations" :key="item.provider_value" class="video-call-payload__input-card">
          <img
            v-if="item.preview_url !== null && item.media_type === 'image'"
            :class="{ 'video-call-payload__input-card-image--reference': item.source === 'provider_asset' }"
            :src="item.preview_url"
            :alt="inputLabel(item)"
          >
          <video v-else-if="item.preview_url !== null" controls preload="metadata">
            <source :src="item.preview_url" type="video/mp4">
          </video>
          <div v-else class="video-call-payload__provider-asset" :aria-label="inputLabel(item)">
            <span>{{ t('visualGameAdmin.llm.calls.detail.video.providerAssetId') }}</span>
            <code>{{ item.provider_value }}</code>
          </div>
          <figcaption>
            <strong>{{ inputLabel(item) }}</strong>
            <span>{{ inputAssetLabel(item) }}</span>
            <a v-if="item.preview_url !== null" :href="item.preview_url" target="_blank" rel="noreferrer">{{ openInputLabel(item) }}</a>
          </figcaption>
        </figure>
      </div>
    </section>

    <details class="video-call-payload__raw">
      <summary>{{ t('visualGameAdmin.llm.calls.detail.video.rawRequest') }}</summary>
      <LongText :label="t('visualGameAdmin.llm.calls.detail.video.rawRequest')" :text="requestJson" :copy-text="requestJson" />
    </details>
  </section>

  <section class="video-call-payload">
    <header class="video-call-payload__heading">
      <div>
        <span class="video-call-payload__eyebrow">{{ t('visualGameAdmin.llm.calls.detail.video.response.eyebrow') }}</span>
        <h3>{{ t('visualGameAdmin.llm.calls.detail.video.response.title') }}</h3>
      </div>
    </header>

    <p v-if="response === null" class="video-call-payload__empty">
      {{ t('visualGameAdmin.llm.calls.detail.video.noResponse') }}
    </p>
    <template v-else-if="status === 'error' && provider === 'seedance'">
      <dl class="video-call-payload__error-facts">
        <div><dt>{{ t('visualGameAdmin.llm.calls.detail.video.errorCode') }}</dt><dd class="vga-error">{{ response.error.code }}</dd></div>
        <div><dt>{{ t('visualGameAdmin.llm.calls.detail.video.errorType') }}</dt><dd>{{ response.error.type }}</dd></div>
        <div><dt>{{ t('visualGameAdmin.llm.calls.detail.video.errorParameter') }}</dt><dd>{{ response.error.param }}</dd></div>
        <div class="video-call-payload__wide"><dt>{{ t('visualGameAdmin.llm.calls.detail.video.errorMessage') }}</dt><dd class="vga-error">{{ response.error.message }}</dd></div>
      </dl>
    </template>
    <template v-else-if="status === 'error'">
      <LongText :label="t('visualGameAdmin.llm.calls.detail.video.rawResponse')" :text="responseJson" :copy-text="responseJson" />
    </template>
    <template v-else>
      <div class="video-call-payload__result-grid">
        <div v-if="outputUrl" class="video-call-payload__video-preview">
          <video controls preload="metadata" :poster="startFrameUrl">
            <source :src="outputUrl" type="video/mp4">
          </video>
        </div>
        <div v-else class="video-call-payload__unavailable-output">
          <strong>{{ t('visualGameAdmin.llm.calls.detail.video.storedOutputUnavailable.title') }}</strong>
          <p>{{ t('visualGameAdmin.llm.calls.detail.video.storedOutputUnavailable.description') }}</p>
        </div>
        <dl class="video-call-payload__result-facts">
          <div v-for="item in responseFacts" :key="item.label"><dt>{{ item.label }}</dt><dd>{{ item.value }}</dd></div>
        </dl>
      </div>
      <figure v-if="endFrameUrl" class="video-call-payload__end-frame">
        <img :src="endFrameUrl" :alt="t('visualGameAdmin.llm.calls.detail.video.storedEndFrame')">
        <figcaption>{{ t('visualGameAdmin.llm.calls.detail.video.storedEndFrame') }}</figcaption>
      </figure>
    </template>

    <details class="video-call-payload__raw">
      <summary>{{ t('visualGameAdmin.llm.calls.detail.video.rawResponse') }}</summary>
      <LongText :label="t('visualGameAdmin.llm.calls.detail.video.rawResponse')" :text="responseJson" :copy-text="responseJson" />
    </details>
  </section>
</template>

<style scoped>
.video-call-payload {
  display: grid;
  gap: var(--drama-space-lg);
}

.video-call-payload h3,
.video-call-payload h4,
.video-call-payload p,
.video-call-payload figure {
  margin: 0;
}

.video-call-payload__heading,
.video-call-payload__input-section > header {
  display: flex;
  align-items: start;
  justify-content: space-between;
  gap: var(--drama-space-md);
}

.video-call-payload__heading h3 {
  margin-top: var(--drama-space-xs);
}

.video-call-payload__heading > strong,
.video-call-payload__input-section > header > span {
  padding: var(--drama-space-xs) var(--drama-space-sm);
  border-radius: var(--drama-radius-full);
  background: var(--drama-color-surface-alt);
  color: var(--drama-color-text-muted);
  font-size: var(--drama-font-size-xs);
  font-weight: var(--drama-font-weight-medium);
}

.video-call-payload__eyebrow,
.video-call-payload dt,
.video-call-payload__input-card strong,
.video-call-payload figcaption,
.video-call-payload__empty {
  color: var(--drama-color-text-muted);
  font-size: var(--drama-font-size-xs);
}

.video-call-payload__configuration,
.video-call-payload__result-facts,
.video-call-payload__error-facts {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 1px;
  margin: 0;
  overflow: hidden;
  border: 1px solid var(--drama-color-border);
  border-radius: var(--drama-radius-md);
  background: var(--drama-color-border);
}

.video-call-payload__configuration > div,
.video-call-payload__result-facts > div,
.video-call-payload__error-facts > div {
  display: grid;
  gap: var(--drama-space-xs);
  min-width: 0;
  padding: var(--drama-space-md);
  background: var(--drama-color-surface);
}

.video-call-payload dd {
  margin: 0;
  overflow-wrap: anywhere;
}

.video-call-payload__wide {
  grid-column: 1 / -1;
}

.video-call-payload__input-section {
  display: grid;
  gap: var(--drama-space-md);
}

.video-call-payload__input-section h4 {
  margin-top: var(--drama-space-xs);
}

.video-call-payload__input-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: var(--drama-space-md);
}

.video-call-payload__input-card {
  display: grid;
  gap: var(--drama-space-sm);
  min-width: 0;
  overflow: hidden;
  border: 1px solid var(--drama-color-border);
  border-radius: var(--drama-radius-md);
  background: var(--drama-color-surface-alt);
}

.video-call-payload__input-card img {
  display: block;
  width: 100%;
  aspect-ratio: 16 / 9;
  object-fit: cover;
  background: var(--drama-color-surface);
}

.video-call-payload__input-card video {
  display: block;
  width: 100%;
  aspect-ratio: 16 / 9;
  object-fit: cover;
  border-radius: var(--drama-radius-md);
  background: var(--drama-color-surface-alt);
}

.video-call-payload__provider-asset {
  display: grid;
  align-content: center;
  gap: var(--drama-space-sm);
  width: 100%;
  aspect-ratio: 16 / 9;
  padding: var(--drama-space-lg);
  background: var(--drama-color-surface);
}

.video-call-payload__provider-asset span,
.video-call-payload__provider-asset code {
  overflow-wrap: anywhere;
}

.video-call-payload__provider-asset span {
  color: var(--drama-color-text-muted);
  font-size: var(--drama-font-size-xs);
}

.video-call-payload__provider-asset code {
  font-family: var(--vgs-font-mono);
  font-size: var(--drama-font-size-sm);
}

.video-call-payload__input-card-image--reference {
  object-fit: contain;
}

.video-call-payload__input-card figcaption {
  padding: var(--drama-space-md);
}

.video-call-payload__input-card figcaption {
  display: grid;
  gap: var(--drama-space-xs);
}

.video-call-payload__input-card a,
.video-call-payload__input-card code {
  overflow-wrap: anywhere;
}

.video-call-payload__input-card details {
  display: grid;
  gap: var(--drama-space-xs);
}

.video-call-payload__input-card summary,
.video-call-payload__input-card code {
  color: var(--drama-color-text-muted);
  font-family: var(--vgs-font-mono);
  font-size: var(--drama-font-size-xs);
}

.video-call-payload__result-grid {
  display: grid;
  grid-template-columns: minmax(0, 1.3fr) minmax(240px, 0.7fr);
  gap: var(--drama-space-lg);
}

.video-call-payload__video-preview,
.video-call-payload__unavailable-output {
  display: grid;
  min-height: 220px;
  overflow: hidden;
  border: 1px solid var(--drama-color-border);
  border-radius: var(--drama-radius-md);
  background: var(--drama-color-surface-alt);
}

.video-call-payload__video-preview video {
  display: block;
  width: 100%;
  height: 100%;
  background: var(--drama-color-surface);
}

.video-call-payload__unavailable-output {
  place-content: center;
  gap: var(--drama-space-sm);
  padding: var(--drama-space-lg);
}

.video-call-payload__unavailable-output p {
  color: var(--drama-color-text-muted);
  font-size: var(--drama-font-size-sm);
  line-height: 1.6;
}

.video-call-payload__result-facts {
  grid-template-columns: repeat(2, minmax(0, 1fr));
  align-content: start;
}

.video-call-payload__end-frame {
  display: grid;
  gap: var(--drama-space-sm);
  max-width: 320px;
}

.video-call-payload__end-frame img {
  display: block;
  width: 100%;
  border: 1px solid var(--drama-color-border);
  border-radius: var(--drama-radius-md);
}

.video-call-payload__raw {
  display: grid;
  gap: var(--drama-space-sm);
}

.video-call-payload__raw summary {
  cursor: pointer;
  color: var(--drama-color-text-muted);
  font-size: var(--drama-font-size-sm);
}

@media (max-width: 760px) {
  .video-call-payload__configuration,
  .video-call-payload__error-facts,
  .video-call-payload__result-facts,
  .video-call-payload__result-grid,
  .video-call-payload__input-grid {
    grid-template-columns: 1fr;
  }

  .video-call-payload__wide {
    grid-column: auto;
  }
}
</style>
